import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { ChoiceGroup } from '../../../shared/components/ui/ChoiceGroup';

export type ValidationStatus = 'valid' | 'invalid' | 'warning' | undefined;

interface FormInputProps {
  brand: BrandConfig;
  label: string;
  name: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onBlur?: () => void;
  type?: 'text' | 'email' | 'tel' | 'number' | 'url' | 'password' | 'date' | 'datetime-local' | 'time';
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helpText?: string;
  /**
   * Field validation status for real-time feedback.
   * - 'valid': green checkmark (show immediately on correction)
   * - 'invalid': red border + message (show after blur or Next click)
   * - 'warning': yellow indicator + message
   * - undefined: no status shown (default)
   */
  validationStatus?: ValidationStatus;
  validationMessage?: string;
  /** Inline content rendered inside the <label>, after the text + required asterisk. */
  labelAdornment?: React.ReactNode;
  /**
   * R8 (SPEC-SOR-321) field association: the blocking-message pair for this
   * control, spread straight from `offendingFieldAria(controlId)`. Kebab keys
   * so the spread is direct; `undefined` omits the attribute, which is the
   * "both attributes disappear" half.
   */
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

function getStatusBorderColor(
  status: ValidationStatus,
  error: string | undefined,
  brand: BrandConfig,
): string {
  if (error) return '#EF4444';
  switch (status) {
    case 'valid': return brand.colors.success || '#22C55E';
    case 'invalid': return '#EF4444';
    case 'warning': return brand.colors.warning || '#F59E0B';
    default: return brand.colors.border;
  }
}

const FormInput: React.FC<FormInputProps> = ({
  brand,
  label,
  name,
  value,
  onChange,
  onBlur,
  type = 'text',
  placeholder,
  required = false,
  disabled = false,
  error,
  helpText,
  validationStatus,
  validationMessage,
  labelAdornment,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}) => {
  const borderColor = getStatusBorderColor(validationStatus, error, brand);
  const showValid = validationStatus === 'valid' && !error;
  const showWarning = validationStatus === 'warning' && !error;
  const effectiveError = error || (validationStatus === 'invalid' ? validationMessage : undefined);

  return (
    <div className="mb-4">
      <label
        htmlFor={name}
        className="flex items-center gap-2 text-sm font-medium mb-1"
        style={{ color: brand.colors.text }}
      >
        <span>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
        {labelAdornment}
      </label>
      <div className="relative">
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : ''
          } ${effectiveError ? 'border-red-500' : ''}`}
          style={{
            borderColor,
            color: brand.colors.text,
            paddingRight: showValid || showWarning ? '2rem' : undefined,
            // @ts-expect-error CSS custom property
            '--tw-ring-color': brand.colors.primary,
          }}
        />
        {/* Validation status indicator */}
        {showValid && (
          <span
            className="absolute right-2 top-1/2 -translate-y-1/2 text-sm"
            style={{ color: brand.colors.success || '#22C55E' }}
          >
            &#10003;
          </span>
        )}
        {showWarning && (
          <span
            className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-bold"
            style={{ color: brand.colors.warning || '#F59E0B' }}
          >
            !
          </span>
        )}
      </div>
      {effectiveError && (
        <p className="mt-1 text-xs text-red-500">{effectiveError}</p>
      )}
      {showWarning && validationMessage && (
        <p className="mt-1 text-xs" style={{ color: brand.colors.warning || '#F59E0B' }}>
          {validationMessage}
        </p>
      )}
      {helpText && !effectiveError && !showWarning && (
        <p className="mt-1 text-xs" style={{ color: brand.colors.textLight }}>
          {helpText}
        </p>
      )}
    </div>
  );
};

interface FormTextareaProps {
  brand: BrandConfig;
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  rows?: number;
  error?: string;
  helpText?: string;
}

export const FormTextarea: React.FC<FormTextareaProps> = ({
  brand,
  label,
  name,
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  rows = 3,
  error,
  helpText,
}) => {
  return (
    <div className="mb-4">
      <label
        htmlFor={name}
        className="block text-sm font-medium mb-1"
        style={{ color: brand.colors.text }}
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        rows={rows}
        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 resize-none ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : ''
        } ${error ? 'border-red-500' : ''}`}
        style={{
          borderColor: error ? '#EF4444' : brand.colors.border,
          color: brand.colors.text,
          // @ts-expect-error CSS custom property
          '--tw-ring-color': brand.colors.primary,
        }}
      />
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
      {helpText && !error && (
        <p className="mt-1 text-xs" style={{ color: brand.colors.textLight }}>
          {helpText}
        </p>
      )}
    </div>
  );
};

interface FormSelectProps {
  brand: BrandConfig;
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helpText?: string;
  /** See FormInput — warning renders an amber border + validationMessage in amber. */
  validationStatus?: ValidationStatus;
  validationMessage?: string;
  /** Inline content rendered inside the <label>, after the text + required asterisk. */
  labelAdornment?: React.ReactNode;
  /** See FormInput — the R8 blocking-message pair, spread from `offendingFieldAria(controlId)`. */
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

export const FormSelect: React.FC<FormSelectProps> = ({
  brand,
  label,
  name,
  value,
  onChange,
  options,
  required = false,
  disabled = false,
  error,
  helpText,
  validationStatus,
  validationMessage,
  labelAdornment,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}) => {
  const borderColor = getStatusBorderColor(validationStatus, error, brand);
  const showWarning = validationStatus === 'warning' && !error;
  return (
    <div className="mb-4">
      <label
        htmlFor={name}
        className="flex items-center gap-2 text-sm font-medium mb-1"
        style={{ color: brand.colors.text }}
      >
        <span>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
        {labelAdornment}
      </label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : ''
        } ${error ? 'border-red-500' : ''}`}
        style={{
          borderColor,
          color: brand.colors.text,
          // @ts-expect-error CSS custom property
          '--tw-ring-color': brand.colors.primary,
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
      {showWarning && validationMessage && (
        <p className="mt-1 text-xs" style={{ color: brand.colors.warning || '#F59E0B' }}>
          {validationMessage}
        </p>
      )}
      {helpText && !error && !showWarning && (
        <p className="mt-1 text-xs" style={{ color: brand.colors.textLight }}>
          {helpText}
        </p>
      )}
    </div>
  );
};

interface FormCheckboxProps {
  brand: BrandConfig;
  label: string;
  name: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  helpText?: string;
}

export const FormCheckbox: React.FC<FormCheckboxProps> = ({
  brand,
  label,
  name,
  checked,
  onChange,
  disabled = false,
  helpText,
}) => {
  return (
    <div className="mb-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          name={name}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="w-4 h-4 rounded border focus:ring-2"
          style={{
            borderColor: brand.colors.border,
            accentColor: brand.colors.primary,
          }}
        />
        <span className="text-sm" style={{ color: brand.colors.text }}>
          {label}
        </span>
      </label>
      {helpText && (
        <p className="mt-1 text-xs ml-6" style={{ color: brand.colors.textLight }}>
          {helpText}
        </p>
      )}
    </div>
  );
};

interface FormRadioGroupProps {
  brand: BrandConfig;
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  helpText?: string;
}

export const FormRadioGroup: React.FC<FormRadioGroupProps> = ({
  brand,
  label,
  name,
  value,
  onChange,
  options,
  disabled = false,
  helpText,
}) => {
  return (
    <ChoiceGroup question={label} className="mb-4" legendClassName="mb-2">
      <div className="flex gap-4">
        {options.map((option) => (
          <label key={option.value} className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={onChange}
              disabled={disabled}
              className="w-4 h-4"
              style={{ accentColor: brand.colors.primary }}
            />
            <span className="text-sm" style={{ color: brand.colors.text }}>
              {option.label}
            </span>
          </label>
        ))}
      </div>
      {helpText && (
        <p className="mt-1 text-xs" style={{ color: brand.colors.textLight }}>
          {helpText}
        </p>
      )}
    </ChoiceGroup>
  );
};

export default FormInput;
